import React from 'react';
import { Badge, cx, Icon, LangDot } from '../lib/shared';

interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  width?: number;
  children: React.ReactNode;
  ariaLabel?: string;
}

export function ModalShell({ open, onClose, width = 540, children, ariaLabel }: ModalShellProps) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-6"
      role="dialog"
      aria-label={ariaLabel}
    >
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />
      <div
        className="relative bg-white rounded-[14px] shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-[#F3E4C8] flex flex-col max-h-[85vh]"
        style={{ width: '100%', maxWidth: width }}
      >
        {children}
      </div>
    </div>
  );
}

interface ModalHeaderProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  icon?: string;
}

export function ModalHeader({ title, subtitle, onClose, icon }: ModalHeaderProps) {
  return (
    <div className="px-6 py-5 border-b border-[#F7EDDB] flex items-start gap-3">
      {icon && (
        <div className="h-10 w-10 rounded-[10px] grid place-items-center border border-[#F3E4C8] bg-[#FCF8F1] shrink-0">
          <Icon name={icon} size={18} stroke="#121111" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-ink text-[18px]">{title}</div>
        {subtitle && <div className="font-data text-[13px] text-ink/55 mt-0.5">{subtitle}</div>}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="h-9 w-9 rounded-[8px] hover:bg-[#F7EDDB] grid place-items-center -mr-2 -mt-1"
      >
        <Icon name="x" size={18} stroke="#121111" />
      </button>
    </div>
  );
}

interface SearchPickerModalProps<T> {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  items: T[];
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  getMeta?: (item: T) => string;
  initial?: string[];
  multi?: boolean;
  confirmLabel?: string;
  onConfirm: (selectedIds: string[]) => void;
  icon?: string;
}

export function SearchPickerModal<T>({
  open,
  onClose,
  title,
  subtitle,
  items,
  getId,
  getLabel,
  getMeta,
  initial = [],
  multi = true,
  confirmLabel = 'Confirm',
  onConfirm,
  icon = 'search',
}: SearchPickerModalProps<T>) {
  const [q, setQ] = React.useState('');
  const [picked, setPicked] = React.useState(new Set(initial));
  React.useEffect(() => {
    if (open) {
      setQ('');
      setPicked(new Set(initial));
    }
  }, [open, initial]);
  const list = items.filter((it) => {
    const l = getLabel(it).toLowerCase();
    const m = (getMeta?.(it) || '').toLowerCase();
    return !q.trim() || l.includes(q.toLowerCase()) || m.includes(q.toLowerCase());
  });
  const toggle = (id: string) => {
    const next = new Set(picked);
    if (multi) {
      next.has(id) ? next.delete(id) : next.add(id);
    } else {
      next.clear();
      next.add(id);
    }
    setPicked(next);
  };
  return (
    <ModalShell open={open} onClose={onClose} width={620}>
      <ModalHeader title={title} subtitle={subtitle} onClose={onClose} icon={icon} />
      <div className="px-6 py-4 border-b border-[#F7EDDB]">
        <div className="flex items-center gap-3 h-11 rounded-[8px] border border-[#F3E4C8] px-3">
          <Icon name="search" size={18} stroke="#121111" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="flex-1 outline-none bg-transparent font-display text-[14px] placeholder:text-ink/40"
          />
          {q && (
            <button type="button" onClick={() => setQ('')}>
              <Icon name="x" size={14} stroke="#121111" />
            </button>
          )}
        </div>
      </div>
      <div className="overflow-y-auto flex-1 min-h-0 px-2 py-2">
        {list.length === 0 ? (
          <div className="py-10 text-center font-display text-ink/45">No matches</div>
        ) : (
          <ul>
            {list.map((it) => {
              const id = getId(it);
              const isOn = picked.has(id);
              const meta = getMeta?.(it);
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => toggle(id)}
                    className={cx(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-[8px] hover:bg-[#FCF8F1]',
                      isOn && 'bg-[#FCF8F1]'
                    )}
                  >
                    {multi ? (
                      <span
                        className={cx(
                          'h-4 w-4 rounded-[3px] border grid place-items-center',
                          isOn ? 'bg-[#FA500E] border-[#FA500E]' : 'border-ink/30'
                        )}
                      >
                        {isOn && <Icon name="check" size={11} stroke="white" />}
                      </span>
                    ) : (
                      <span
                        className={cx(
                          'h-4 w-4 rounded-full border grid place-items-center',
                          isOn ? 'border-[#FA500E]' : 'border-ink/30'
                        )}
                      >
                        {isOn && <span className="h-2 w-2 rounded-full bg-[#FA500E]" />}
                      </span>
                    )}
                    <span className="flex-1 text-left">
                      <span className="block font-display font-semibold text-ink text-[14px]">
                        {getLabel(it)}
                      </span>
                      {meta && (
                        <span className="block font-data text-[12px] text-ink/55">{meta}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="px-6 py-4 border-t border-[#F7EDDB] flex items-center justify-between">
        <span className="font-data text-[12px] text-ink/55">{picked.size} selected</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-[8px] border border-[#F3E4C8] font-display font-semibold text-[14px] text-ink hover:bg-[#FCF8F1]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm([...picked])}
            className="h-10 px-4 rounded-[8px] bg-[#FA500E] text-white font-display font-bold text-[14px] hover:opacity-90 disabled:opacity-40"
            disabled={!picked.size}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

interface Repository {
  id: string;
  name: string;
  lang: string;
  controls: number;
}

interface SwitchRepoModalProps {
  open: boolean;
  current?: Repository | null;
  repos: Repository[];
  onClose: () => void;
  onPick: (repoId: string) => void;
}

export function SwitchRepoModal({ open, current, repos, onClose, onPick }: SwitchRepoModalProps) {
  const [q, setQ] = React.useState('');
  const list = repos.filter((r) => !q.trim() || r.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <ModalShell open={open} onClose={onClose} width={520}>
      <ModalHeader
        title="Switch repository"
        subtitle="Jump to another repo Cylift is watching"
        onClose={onClose}
        icon="book"
      />
      <div className="px-6 py-4 border-b border-[#F7EDDB]">
        <div className="flex items-center gap-3 h-11 rounded-[8px] border border-[#F3E4C8] px-3">
          <Icon name="search" size={18} stroke="#121111" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a repository…"
            className="flex-1 outline-none bg-transparent font-display text-[14px] placeholder:text-ink/40"
          />
        </div>
      </div>
      <ul className="overflow-y-auto flex-1 min-h-0 px-2 py-2">
        {list.map((r) => {
          const isCurrent = current && r.id === current.id;
          return (
            <li key={r.id}>
              <button
                type="button"
                disabled={isCurrent}
                onClick={() => onPick(r.id)}
                className={cx(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-[8px]',
                  isCurrent ? 'bg-[#FCF8F1] cursor-default' : 'hover:bg-[#FCF8F1]'
                )}
              >
                <Icon name="book" size={16} stroke="#121111" />
                <div className="flex-1 text-left">
                  <div className="font-display font-bold text-ink text-[14px]">{r.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <LangDot lang={r.lang} />
                    <span className="text-ink/30">·</span>
                    <span className="font-data text-[12px] text-ink/55">{r.controls} controls</span>
                  </div>
                </div>
                {isCurrent ? (
                  <Badge tone="info">current</Badge>
                ) : (
                  <Icon name="chevron-right" size={14} stroke="rgba(18,17,17,0.4)" />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </ModalShell>
  );
}

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel = 'Confirm',
  danger,
}: ConfirmModalProps) {
  return (
    <ModalShell open={open} onClose={onClose} width={460}>
      <ModalHeader title={title} onClose={onClose} icon={danger ? 'alert-tri' : 'shield'} />
      <div className="px-6 py-5 font-display text-[14px] text-ink/75">{body}</div>
      <div className="px-6 py-4 border-t border-[#F7EDDB] flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-10 px-4 rounded-[8px] border border-[#F3E4C8] font-display font-semibold text-[14px] text-ink hover:bg-[#FCF8F1]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className={cx(
            'h-10 px-4 rounded-[8px] font-display font-bold text-[14px] text-white hover:opacity-90',
            danger ? 'bg-[#F40B0B]' : 'bg-[#FA500E]'
          )}
        >
          {confirmLabel}
        </button>
      </div>
    </ModalShell>
  );
}

interface UninstallModalProps {
  open: boolean;
  repo?: Repository | null;
  onClose: () => void;
  onConfirm: () => void;
}

export function UninstallModal({ open, repo, onClose, onConfirm }: UninstallModalProps) {
  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={`Uninstall ${repo?.name || 'repository'}?`}
      body="Cylift will stop watching this repository. Existing controls and risk acceptances are kept and can be re-applied later."
      confirmLabel="Uninstall"
      danger
    />
  );
}

interface NavigateFunction {
  (path: string): void;
}

interface CommandPaletteData {
  repos: { repos: Repository[] };
  controls: { controls: { id: string; name: string; category: string }[] };
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  navigate: NavigateFunction;
  data?: CommandPaletteData | null;
}

export function CommandPalette({ open, onClose, navigate, data }: CommandPaletteProps) {
  const [q, setQ] = React.useState('');
  React.useEffect(() => {
    if (open) setQ('');
  }, [open]);
  if (!data) return null;

  const items = [
    {
      kind: 'Go',
      label: 'Dashboard',
      sub: 'KPIs & KRIs',
      icon: 'dashboard',
      to: 'dashboard',
    },
    {
      kind: 'Go',
      label: 'Repositories',
      sub: 'All repos',
      icon: 'book',
      to: 'repositories',
    },
    {
      kind: 'Go',
      label: 'Controls',
      sub: 'Control library',
      icon: 'shield',
      to: 'controls',
    },
    {
      kind: 'Go',
      label: 'Risk acceptances',
      sub: 'Exceptions',
      icon: 'alert-tri',
      to: 'risk-acceptances',
    },
    {
      kind: 'Go',
      label: 'Tokens',
      sub: 'API tokens',
      icon: 'settings',
      to: 'tokens',
    },
    {
      kind: 'Action',
      label: 'Add a control',
      sub: 'Create new control',
      icon: 'plus',
      to: 'controls/new',
    },
    {
      kind: 'Action',
      label: 'Create risk acceptance',
      sub: 'Start the wizard',
      icon: 'plus',
      to: 'risk-acceptances/new',
    },
    {
      kind: 'Action',
      label: 'Create API token',
      sub: 'Add a new API token',
      icon: 'plus',
      to: 'tokens/new',
    },
    ...data.repos.repos.map((r) => ({
      kind: 'Repository',
      label: r.name,
      sub: `${r.lang} · ${r.controls} controls`,
      icon: 'book',
      to: `repositories/${r.id}`,
    })),
    ...data.controls.controls.map((c) => ({
      kind: 'Control',
      label: c.name,
      sub: `#${c.id} · ${c.category}`,
      icon: 'shield',
      to: `controls/${c.id}`,
    })),
  ];
  const list = items.filter(
    (it) => !q.trim() || `${it.label} ${it.sub}`.toLowerCase().includes(q.toLowerCase())
  );
  const groups = list.reduce((acc, it) => {
    if (!acc[it.kind]) acc[it.kind] = [];
    acc[it.kind].push(it);
    return acc;
  }, {} as Record<string, typeof items>);

  return (
    <ModalShell open={open} onClose={onClose} width={620}>
      <div className="px-5 h-14 flex items-center gap-3 border-b border-[#F7EDDB]">
        <Icon name="search" size={18} stroke="#121111" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Jump to a page, repo, or control…"
          className="flex-1 outline-none bg-transparent font-display text-[15px] placeholder:text-ink/40"
        />
        <span className="font-data text-[11px] text-ink/40 border border-[#F3E4C8] rounded-[3px] px-1.5 py-0.5">
          esc
        </span>
      </div>
      <div className="overflow-y-auto flex-1 min-h-0">
        {Object.keys(groups).length === 0 && (
          <div className="py-12 text-center font-display text-ink/45">No matches</div>
        )}
        {Object.entries(groups).map(([k, its]) => (
          <div key={k} className="py-2">
            <div className="px-5 py-1.5 font-display text-[10px] uppercase tracking-[0.12em] font-bold text-ink/45">
              {k}
            </div>
            {its.slice(0, 6).map((it, i) => (
              <button
                type="button"
                key={it.to || `modal-item-${i}`}
                onClick={() => {
                  onClose();
                  navigate(it.to);
                }}
                className="w-full flex items-center gap-3 px-5 py-2 hover:bg-[#FCF8F1]"
              >
                <Icon name={it.icon} size={14} stroke="#121111" />
                <span className="flex-1 text-left">
                  <span className="block font-display font-semibold text-ink text-[13px]">
                    {it.label}
                  </span>
                  <span className="block font-data text-[11px] text-ink/55">{it.sub}</span>
                </span>
                <Icon name="chevron-right" size={12} stroke="rgba(18,17,17,0.4)" />
              </button>
            ))}
          </div>
        ))}
      </div>
    </ModalShell>
  );
}